/**
 * Filters Modal - Right-side sliding filter panel
 * Handles opening/closing modal, filter groups, and applying filters
 */
class FiltersModal {
  constructor() {
    this.modal = document.querySelector('[data-modal="filtersModal"]');
    this.openButtons = document.querySelectorAll('[data-modal-open="filtersModal"]');
    this.closeButtons = document.querySelectorAll('[data-modal-close="filtersModal"]');
    this.filterToggles = document.querySelectorAll('[data-filter-toggle]');
    this.applyButton = document.querySelector('[data-apply-filters]');
    this.form = document.getElementById('FiltersModalForm');
    
    this.init();
  }

  init() {
    this.resetRow = this.modal ? this.modal.querySelector('[id^="filtersResetRow"]') : null;

    // Open modal
    this.openButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    });

    // Close modal
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Toggle filter groups
    this.filterToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleGroup(e.currentTarget);
      });
    });

    // Apply filters
    if (this.applyButton) {
      this.applyButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.applyFilters();
      });
    }

    // Reset filters
    const resetBtn = this.modal ? this.modal.querySelector('[data-reset-filters]') : null;
    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.resetFilters();
      });
    }

    // Close on overlay click
    if (this.modal) {
      const overlay = this.modal.querySelector('.filters-modal__overlay');
      if (overlay) {
        overlay.addEventListener('click', () => this.close());
      }
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.classList.contains('is-active')) {
        this.close();
      }
    });

    // Handle filter pills
    this.initFilterPills();

    // Sync reset row visibility with current URL params on open
    this.syncResetRow();
  }

  open() {
    if (this.modal) {
      this.modal.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    } else {
      console.warn('FiltersModal: Modal element not found');
    }
  }

  close() {
    if (this.modal) {
      this.modal.classList.remove('is-active');
      document.body.style.overflow = '';
    }
  }

  toggleGroup(button) {
    const group = button.closest('.filter-group');
    if (group) {
      group.classList.toggle('is-open');
      
      // Update max-height for smooth animation
      const content = group.querySelector('.filter-group__content');
      if (content) {
        if (group.classList.contains('is-open')) {
          content.style.maxHeight = content.scrollHeight + 'px';
        } else {
          content.style.maxHeight = '0';
        }
      }
    }
  }

  initFilterPills() {
    // Handle filter option pills (multiple selection)
    const filterPills = document.querySelectorAll('.filter-pill-option[data-filter-value]');
    filterPills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        pill.classList.toggle('filter-pill-option--active');
        const isActive = pill.classList.contains('filter-pill-option--active');
        pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        this.updateResetRow();
      });
    });

    // Handle color swatches (same logic, different class)
    const swatches = document.querySelectorAll('.filter-swatch[data-filter-value]');
    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        swatch.classList.toggle('filter-swatch--active');
        const isActive = swatch.classList.contains('filter-swatch--active');
        swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        this.updateResetRow();
      });
    });
  }

  /* Show reset row if any filter is active (selected in UI or already in URL) */
  updateResetRow() {
    if (!this.resetRow) return;
    const hasActive = !!this.form.querySelector(
      '.filter-pill-option--active[data-filter-value], .filter-swatch--active[data-filter-value]'
    );
    this.resetRow.classList.toggle('is-visible', hasActive);
  }

  /* Show reset row if current URL already has filter params */
  syncResetRow() {
    if (!this.resetRow) return;
    const params = new URLSearchParams(window.location.search);
    let hasFilters = false;
    params.forEach((_, key) => {
      if (key.startsWith('filter.')) hasFilters = true;
    });
    if (hasFilters) this.resetRow.classList.add('is-visible');
  }

  resetFilters() {
    // Deselect all pills and swatches in the UI
    if (this.form) {
      this.form.querySelectorAll('.filter-pill-option--active').forEach(el => {
        el.classList.remove('filter-pill-option--active');
        el.setAttribute('aria-pressed', 'false');
      });
      this.form.querySelectorAll('.filter-swatch--active').forEach(el => {
        el.classList.remove('filter-swatch--active');
        el.setAttribute('aria-pressed', 'false');
      });
      this.form.querySelectorAll('.filter-price__input').forEach(el => {
        el.value = '';
      });
    }
    // Navigate to the base URL (remove all filter params, keep sort_by)
    const currentUrl = new URL(window.location.href);
    const sort = currentUrl.searchParams.get('sort_by');
    const finalUrl = sort ? `${currentUrl.pathname}?sort_by=${sort}` : currentUrl.pathname;
    window.location.href = finalUrl;
  }

  applyFilters() {
    if (!this.form) return;

    // Get base collection URL (without query params)
    const currentUrl = new URL(window.location.href);
    const baseUrl = currentUrl.pathname;
    
    // Collect active filter pills AND active color swatches
    const activePills = this.form.querySelectorAll(
      '.filter-pill-option--active[data-filter-value], .filter-swatch--active[data-filter-value]'
    );
    
    // Collect price range inputs
    const priceInputs = this.form.querySelectorAll('.filter-price__input');
    
    // Build URL using Shopify's filter system
    const finalParams = new URLSearchParams();
    
    // Group pills by their param_name to handle multiple values correctly
    const filterGroups = {};
    
    activePills.forEach(pill => {
      const paramName = pill.dataset.paramName;
      const value = pill.dataset.value;
      
      if (paramName && value) {
        if (!filterGroups[paramName]) {
          filterGroups[paramName] = [];
        }
        // Avoid duplicates
        if (!filterGroups[paramName].includes(value)) {
          filterGroups[paramName].push(value);
        }
      }
    });

    // Add all filter parameters (multiple values for same key are allowed in Shopify)
    // This handles cases like: filter.v.option.size=37.5&filter.v.option.size=38
    Object.entries(filterGroups).forEach(([paramName, values]) => {
      values.forEach(value => {
        finalParams.append(paramName, value);
      });
    });

    // Add price range filters (only if they have values)
    priceInputs.forEach(input => {
      const value = input.value ? input.value.trim() : '';
      if (input.name && value !== '') {
        finalParams.append(input.name, value);
      }
    });

    // Preserve sort_by from current URL if present
    const currentSort = currentUrl.searchParams.get('sort_by');
    if (currentSort) {
      finalParams.set('sort_by', currentSort);
    }

    // Build final URL
    const queryString = finalParams.toString();
    const finalUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    
    // Navigate to filtered URL
    window.location.href = finalUrl;
  }
}

// Initialize when DOM is ready
(function() {
  function initFiltersModal() {
    // Check if modal exists on page
    const modal = document.querySelector('[data-modal="filtersModal"]');
    if (modal) {
      new FiltersModal();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFiltersModal);
  } else {
    // DOM already loaded
    initFiltersModal();
  }
})();

